import { Link } from 'react-router-dom';
import type { JobGrade } from '../../lib/grading.ts';

interface GradeCardProps {
  jobId: string;
  grade: JobGrade | null;
}

// Two variants:
//  - grade !== null: real numeric score for a corpus job
//  - grade === null: hint pointing at the job-detail Promote button
export function GradeCard({ jobId, grade }: GradeCardProps) {
  if (grade === null) {
    return (
      <div style={hintStyle}>
        <div style={hintLabelStyle}>GRADE</div>
        <div style={{ color: '#B8BDD6', fontSize: 13 }}>
          This job isn't promoted to the corpus yet, so there's no grade.{' '}
          <Link to={`/app/jobs/${jobId}`} style={{ color: '#00E87A', fontWeight: 600 }}>
            Promote to enable grading →
          </Link>
        </div>
      </div>
    );
  }

  const color = scoreColor(grade.scorePct);

  return (
    <div style={cardStyle}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 16, marginBottom: 14 }}>
        <div>
          <div style={hintLabelStyle}>GRADE</div>
          <div style={{ fontFamily: "'Syne', sans-serif", fontSize: 36, fontWeight: 800, color, lineHeight: 1 }}>
            {grade.scorePct}
            <span style={{ fontSize: 18, color: '#525870', marginLeft: 4 }}>%</span>
          </div>
        </div>
        <div style={{ color: '#525870', fontSize: 12, fontFamily: "'JetBrains Mono', monospace" }}>
          {grade.totalGraded} graded · {grade.pageGrades.length} total
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 8 }}>
        <Stat label="Exact" value={grade.totalExact} accent="#00E87A" />
        <Stat label="Sub mismatch" value={grade.totalSubMismatch} accent="#C49BFF" />
        <Stat label="Discipline mismatch" value={grade.totalDisciplineMismatch} accent="#FF6B7A" />
        <Stat label="No prediction" value={grade.totalNoPrediction} accent="#525870" />
        <Stat label="No truth" value={grade.totalNoTruth} accent="#525870" />
        <Stat label="Skipped" value={grade.totalSkipped} accent="#525870" />
      </div>
    </div>
  );
}

function Stat({ label, value, accent }: { label: string; value: number; accent: string }) {
  return (
    <div
      style={{
        padding: '8px 10px',
        background: '#0D0F1C',
        border: `1px solid ${value > 0 ? accent : 'rgba(255,255,255,0.06)'}`,
        borderRadius: 6,
      }}
    >
      <div style={{ ...hintLabelStyle, marginBottom: 2 }}>{label}</div>
      <div style={{ color: value > 0 ? accent : '#525870', fontSize: 18, fontWeight: 600 }}>{value}</div>
    </div>
  );
}

function scoreColor(pct: number): string {
  if (pct >= 90) return '#00E87A';
  if (pct >= 70) return '#C49BFF';
  if (pct >= 50) return '#FFD56B';
  return '#FF6B7A';
}

const cardStyle: React.CSSProperties = {
  padding: 16,
  background: '#0D0F1C',
  border: '1px solid rgba(255,255,255,0.08)',
  borderRadius: 10,
  marginBottom: 20,
};

const hintStyle: React.CSSProperties = {
  padding: 14,
  background: '#0D0F1C',
  border: '1px solid rgba(255,255,255,0.06)',
  borderRadius: 8,
  marginBottom: 20,
};

const hintLabelStyle: React.CSSProperties = {
  color: '#525870',
  fontSize: 10,
  fontFamily: "'JetBrains Mono', monospace",
  letterSpacing: '0.1em',
  textTransform: 'uppercase',
};
